import { useTranslation } from '@crew/modules/i18n'
import { useModal } from 'components/layouts/modal/useModal'
import { FC, memo, useCallback } from 'react'
import MaterialSymbolsCrown from '~icons/material-symbols/crown'
import { CrewNavigateToPaidPlanDialog } from '../crewNavigateToPaidPlanDialog/crewNavigateToPaidPlanDialog'
import classNames from 'classnames'

type CrewPaidPlanFeatureProps = {
  /** 有償機能が使えるかどうか */
  isPaidFeatureEnabled: boolean
  /** 有償機能試用期間中かどうか */
  isPaidFeatureTrial: boolean
  /** 機能のラベルテキスト */
  label: string
  /** アイコンの幅 */
  width?: number
  /** アイコンの高さ */
  height?: number
  /** クリック時のハンドラ（有償プランの場合に呼ばれる通常動作） */
  onClick: () => void
}

const DEFAULT_ICON_WIDTH = 20
const DEFAULT_ICON_HEIGHT = 20

/**
 * 有償プラン限定機能を表示するコンポーネント
 * フリープランの場合は、ラベル or アイコンをクリックで有償プランへの誘導ダイアログを表示する
 * 有償プランの場合は、通常通りラベルクリック時にonClickが呼ばれる
 */
export const CrewPaidPlanFeature: FC<CrewPaidPlanFeatureProps> = memo(
  (props) => {
    const { t } = useTranslation()

    // 有償プランへの誘導ダイアログ
    const [
      isNavigateToPaidPlanDialogOpen,
      openNavigateToPaidPlanDialog,
      closeNavigateToPaidPlanDialog,
    ] = useModal()

    // クリック時の処理
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!props.isPaidFeatureEnabled && !props.isPaidFeatureTrial) {
          // 有償機能が使えない＆試用期間外の場合はダイアログを開く
          openNavigateToPaidPlanDialog()
        } else {
          // 有償プランの場合は通常の処理を実行
          props.onClick?.()
        }
      },
      [props, openNavigateToPaidPlanDialog]
    )

    // propsで指定がなければデフォルトサイズを使用する
    const width = props.width ?? DEFAULT_ICON_WIDTH
    const height = props.height ?? DEFAULT_ICON_HEIGHT

    return (
      <>
        <div
          className={classNames({
            'cursor-pointer': !props.isPaidFeatureEnabled,
          })}
          onClick={handleClick}
        >
          <div className="flex items-center gap-2">
            <div className="flex-1">{props.label}</div>
            {/* 有償プランがつかえない場合または、試用期間中はアイコンを表示 */}
            {(!props.isPaidFeatureEnabled || props.isPaidFeatureTrial) && (
              <div className="flex" title={t('label.onlyForPaidPlan')}>
                <MaterialSymbolsCrown
                  className="text-crew-yellow-3-light dark:text-crew-yellow-3-dark"
                  width={width}
                  height={height}
                />
              </div>
            )}
          </div>
        </div>

        {/* 有償プランへの誘導ダイアログ */}
        <CrewNavigateToPaidPlanDialog
          isOpen={isNavigateToPaidPlanDialogOpen}
          onClose={closeNavigateToPaidPlanDialog}
        />
      </>
    )
  }
)
